import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type {
  IntegrationBusinessUnit,
  IntegrationValidationError,
  PatchIntegrationBusinessUnitsUuid200
} from 'api';
import {
  getGetIntegrationBusinessUnitsQueryKey,
  usePatchIntegrationBusinessUnitsUuid
} from 'api';
import type { RenameBusinessUnitFormValues } from 'validators/businessUnit';
import { renameBusinessUnitSchema } from 'validators/businessUnit';

import { useOrganisationId } from 'hooks/context';

import {
  RENAME_BUSINESS_UNIT_GENERIC_ERROR,
  RENAME_BUSINESS_UNIT_SUCCESS
} from './constants';

import { extractIntegrationValidationError } from '../../utils/extractIntegrationValidationError';
import { useBusinessUnitsListSync } from '../BusinessUnitsListSyncContext';

type UseRenameBusinessUnitFormArgs = {
  businessUnit: IntegrationBusinessUnit;
  onClose: () => void;
};

export type UseRenameBusinessUnitForm = {
  form: UseFormReturn<RenameBusinessUnitFormValues>;
  submit: ReturnType<
    UseFormReturn<RenameBusinessUnitFormValues>['handleSubmit']
  >;
  cancel: () => void;
  isSubmitting: boolean;
};

export const useRenameBusinessUnitForm = ({
  businessUnit,
  onClose
}: UseRenameBusinessUnitFormArgs): UseRenameBusinessUnitForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const { onBusinessUnitRenamed } = useBusinessUnitsListSync();
  const renameMutation = usePatchIntegrationBusinessUnitsUuid();

  const form = useForm<RenameBusinessUnitFormValues>({
    mode: 'onChange',
    resolver: yupResolver(
      renameBusinessUnitSchema
    ) as unknown as Resolver<RenameBusinessUnitFormValues>,
    defaultValues: { name: businessUnit.name }
  });

  const handleSuccess = (response: PatchIntegrationBusinessUnitsUuid200) => {
    queryClient.invalidateQueries({
      queryKey: getGetIntegrationBusinessUnitsQueryKey(organisationId)
    });
    onBusinessUnitRenamed(response.data);
    openNotification({
      kind: 'success',
      message: RENAME_BUSINESS_UNIT_SUCCESS
    });
    cancel();
  };

  const handleError = (error: IntegrationValidationError | null) => {
    openNotification({
      kind: 'error',
      message: extractIntegrationValidationError(
        error,
        RENAME_BUSINESS_UNIT_GENERIC_ERROR
      )
    });
  };

  const submit = form.handleSubmit((values) => {
    renameMutation.mutate(
      {
        organisationId,
        uuid: businessUnit.uuid,
        data: { name: values.name.trim() }
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  });

  const cancel = () => {
    form.reset();
    onClose();
  };

  return {
    form,
    submit,
    cancel,
    isSubmitting: renameMutation.isPending
  };
};
